import { type AriaProps } from '@common/components/controls/ariaHelpers/types';

/**
 * State for generating dialog ARIA attributes.
 * Following W3C Dialog pattern: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 */
export interface DialogState {
  titleId?: string;
  descriptionId?: string;
  isModal?: boolean;
}

/**
 * Generates ARIA attributes for a dialog element.
 * aria-modal is REQUIRED for modal dialogs.
 *
 * @param state - The current dialog state
 * @returns ARIA props to spread onto the dialog element
 */
export function getDialogAriaProps(state: DialogState): AriaProps {
  const { titleId, descriptionId, isModal = true } = state;

  const props: AriaProps = {
    role: 'dialog',
  };

  // Modal dialogs MUST have aria-modal="true"
  if (isModal) {
    props['aria-modal'] = true;
  }

  // Link to title (REQUIRED for accessibility)
  if (titleId) {
    props['aria-labelledby'] = titleId;
  }

  // Link to description (optional but recommended)
  if (descriptionId) {
    props['aria-describedby'] = descriptionId;
  }

  return props;
}

/**
 * Generates unique IDs for dialog title and description.
 *
 * @param dialogId - The ID of the dialog element
 * @returns Object with titleId and descriptionId
 */
export function getDialogIds(dialogId: string): {
  titleId: string;
  descriptionId: string;
} {
  return {
    titleId: `${dialogId}-title`,
    descriptionId: `${dialogId}-description`,
  };
}
